/*
 OK FBI — Help Assistant Floating Widget ("مساعد OK")

 - Fixed LEFT-SIDE floating button (desktop lower-left, mobile bottom-left).
 - Click → opens a compact chat panel (Arabic RTL). Not full-screen on desktop.
 - Uses /api/help/chat (public + authenticated). No leak of private data.
 - Hidden in Stream Mode by default.
*/
import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const SUGGESTIONS = [
  "كيف أبدأ اللعب؟",
  "أين رقم المحقق؟",
  "كيف أستجوب مشتبهاً؟",
  "كيف أواجه بالدليل؟",
  "أين ملاحظاتي؟",
  "ما هو XP؟",
];

function pageHintFor(pathname) {
  if (!pathname) return "";
  if (pathname === "/") return "الصفحة الرئيسية";
  if (pathname.startsWith("/login")) return "تسجيل الدخول";
  if (pathname.startsWith("/app")) return "لوحة المحقق";
  if (pathname.startsWith("/cases/") || pathname.startsWith("/case/")) return "قضية";
  if (pathname.startsWith("/cases")) return "قائمة القضايا";
  if (pathname.startsWith("/host/team")) return "لوحة المالك";
  if (pathname.startsWith("/host/case-builder")) return "بناء القضايا";
  if (pathname.startsWith("/notes")) return "لوحة الملاحظات";
  if (pathname.startsWith("/leaderboard")) return "لوحة المتصدرين";
  if (pathname.startsWith("/profile")) return "الملف الشخصي";
  if (pathname.startsWith("/settings")) return "الإعدادات";
  if (pathname.startsWith("/terms")) return "الشروط";
  if (pathname.startsWith("/privacy")) return "الخصوصية";
  return pathname;
}

// Hide list — DO NOT show on these routes (avoid capturing OBS stream / overlay)
// Also hidden on /host/* because CueCardsDock ("مساعد المقدم") already sits at
// the same bottom-left position on those pages — prevents duplicate stacked
// floating assistants for STREAMER/OWNER during live broadcast.
const HIDDEN_ROUTES = ["/overlay", "/host"];

export default function HelpAssistant() {
  const { pathname } = useLocation();
  const hidden = HIDDEN_ROUTES.some((r) => pathname === r || pathname.startsWith(r + "/"));
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scroller = useRef(null);
  const inputRef = useRef(null);

  // Welcome message the first time the panel opens in a session
  useEffect(() => {
    if (open && turns.length === 0) {
      setTurns([{
        role: "assistant",
        text: "هلا فيك في مساعد OK! أنا هنا أساعدك تستخدم المنصة: التسجيل، القضايا، الاستجواب، الملاحظات، الأدلة، الباقات… اسألني بحرية.",
      }]);
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open, turns.length]);

  useEffect(() => {
    if (!scroller.current) return;
    scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [turns, busy]);

  const send = useCallback(async (msgOverride) => {
    const msg = (msgOverride ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setTurns((t) => [...t, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/help/chat`, {
        message: msg, session_id: sessionId || undefined,
        page: pageHintFor(pathname),
      });
      setSessionId(data.session_id);
      setTurns((t) => [...t, { role: "assistant", text: data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setTurns((t) => [...t, {
        role: "assistant",
        text: typeof detail === "string" ? detail : "تعذّر الرد الآن، حاول مرة أخرى.",
      }]);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [input, busy, sessionId, pathname]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (hidden) return null;

  return createPortal(
    <>
      {/* Floating button (lower-LEFT) */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(true)}
            data-testid="help-fab"
            title="مساعد OK"
            className="fixed z-[70] bottom-4 left-4 md:bottom-6 md:left-6
                       flex items-center gap-2 px-4 py-2.5
                       bg-black/85 border border-gold/50 text-gold
                       hover:border-gold hover:bg-gold/10 transition-colors
                       backdrop-blur-md font-cairo text-xs md:text-sm"
            style={{ boxShadow: "0 4px 24px rgba(217,166,80,0.15)" }}
          >
            <MessageCircle size={16} />
            <span className="hidden md:inline">مساعد OK</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel (opens from LEFT) */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, x: -40, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: -40, y: 20 }}
            transition={{ duration: 0.28 }}
            data-testid="help-panel"
            className="fixed z-[70] bottom-4 left-4 md:bottom-6 md:left-6
                       w-[92vw] md:w-[380px] max-h-[70vh] md:max-h-[560px]
                       flex flex-col bg-[#0b0b0e]/98 border border-gold/40
                       backdrop-blur-xl font-cairo"
            style={{ boxShadow: "0 12px 48px rgba(0,0,0,0.6)" }}
            dir="rtl"
          >
            {/* Header */}
            <div className="flex items-center gap-2 p-3 border-b border-metal">
              <div className="w-8 h-8 bg-gold/15 border border-gold/40 flex items-center justify-center text-gold">
                <Sparkles size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.35em] text-gold">OK · HELP</div>
                <div className="text-white text-sm">مساعد OK</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="help-close-btn"
                className="p-1.5 text-zinc-400 hover:text-white">
                <X size={16} />
              </button>
            </div>

            {/* Transcript */}
            <div ref={scroller} className="flex-1 overflow-y-auto p-3 space-y-2.5">
              {turns.map((t, i) => (
                <div key={i} className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 text-[13px] leading-relaxed border ${
                    t.role === "user"
                      ? "bg-gold/10 border-gold/40 text-white"
                      : "bg-zinc-900/80 border-metal text-zinc-100"
                  }`}>
                    {t.text}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="px-3 py-2 bg-zinc-900/80 border border-metal text-zinc-400 text-xs inline-flex items-center gap-2">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                    يفكّر…
                  </div>
                </div>
              )}
            </div>

            {/* Quick actions */}
            {turns.length <= 1 && (
              <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} disabled={busy}
                    data-testid="help-suggestion-btn"
                    className="text-[11px] px-2 py-1 border border-metal text-zinc-300 hover:border-gold hover:text-gold transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input bar */}
            <div className="border-t border-metal p-2.5 flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, 1000))}
                onKeyDown={onKeyDown}
                disabled={busy}
                data-testid="help-input"
                placeholder="اسأل عن أي شيء في المنصة…"
                className="flex-1 bg-zinc-950 border border-metal focus:border-gold px-3 py-2 text-white text-sm outline-none placeholder-zinc-600"
                style={{ direction: "rtl" }}
              />
              <button onClick={() => send()} disabled={busy || !input.trim()}
                data-testid="help-send-btn"
                className="btn-primary px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
}
